/**
 * The company's own facts, for the documents that have to state them.
 *
 * Four of these are printed verbatim onto a board minute or a payment request,
 * so this screen exists to stop them being retyped per document — which is how
 * two filings end up disagreeing about an organisation number.
 *
 * ## Par value is not the share price
 *
 * The field people get wrong. Pålydende is what a share is worth on the
 * register; the price in an issuance is what an investor paid for it, and the
 * difference is overkurs. The register knows the second and cannot derive the
 * first, so the board minutes refuse to state a capital figure until this is
 * filled in — see `types/company.ts`.
 */
import { useEffect, useState } from 'react'
import { parseKr } from '@/shared/util/money'
import { EMPTY_COMPANY, type BoardMember, type CompanyDetails } from '@/types/company'

export function CompanyEditor({
  company,
  readOnly,
  onSave,
}: {
  company: CompanyDetails
  readOnly: boolean
  onSave: (c: CompanyDetails) => Promise<void>
}) {
  const [form, setForm] = useState<CompanyDetails>(company)
  const [par, setPar] = useState(company.parValue === null ? '' : String(company.parValue))
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The document is loaded after the first render, so the form follows it in.
  useEffect(() => {
    setForm(company)
    setPar(company.parValue === null ? '' : String(company.parValue))
  }, [company])

  const set = (patch: Partial<CompanyDetails>) => setForm((f) => ({ ...f, ...patch }))
  const setMember = (i: number, patch: Partial<BoardMember>) =>
    set({ board: form.board.map((m, x) => (x === i ? { ...m, ...patch } : m)) })

  const save = async () => {
    const parsed = par.trim() === '' ? null : parseKr(par)
    if (par.trim() !== '' && parsed === null) {
      setError('That par value is not a number.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave({
        ...form,
        legalName: form.legalName.trim(),
        orgNumber: form.orgNumber.trim(),
        bankAccount: form.bankAccount.trim(),
        parValue: parsed,
        board: form.board.filter((m) => m.name.trim()),
      })
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="company">
      <div className="company__grid">
        <label className="field">
          <span>Legal name</span>
          <input
            className="input"
            value={form.legalName}
            disabled={readOnly}
            placeholder="Skida AS"
            onChange={(e) => set({ legalName: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Organisation number</span>
          <input
            className="input"
            value={form.orgNumber}
            disabled={readOnly}
            placeholder="999 999 999"
            onChange={(e) => set({ orgNumber: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Bank account</span>
          <input
            className="input"
            value={form.bankAccount}
            disabled={readOnly}
            placeholder="1234.56.78901"
            onChange={(e) => set({ bankAccount: e.target.value })}
          />
          <span className="field__note">
            The default a payment request asks investors to pay into. Editable per send.
          </span>
        </label>
        <label className="field">
          <span>Par value per share (kr)</span>
          <input
            className="input"
            inputMode="decimal"
            value={par}
            disabled={readOnly}
            placeholder="0,10"
            onChange={(e) => setPar(e.target.value)}
          />
          <span className="field__note">
            Pålydende, not the price paid. Share capital is shares × this, and the board minutes
            cannot state a capital figure without it.
          </span>
        </label>
      </div>

      <h4 className="recform__legend company__legend">Board</h4>
      <p className="recform__note company__note">
        Printed on the minutes, in this order, with a signature rule under each.
      </p>
      <ul className="holding">
        {form.board.map((m, i) => (
          <li className="holding__row company__member" key={i}>
            <input
              className="input"
              value={m.name}
              disabled={readOnly}
              placeholder="Name"
              onChange={(e) => setMember(i, { name: e.target.value })}
            />
            <input
              className="input"
              value={m.role}
              disabled={readOnly}
              placeholder="Styreleder"
              onChange={(e) => setMember(i, { role: e.target.value })}
            />
            {!readOnly && (
              <button
                className="holding__remove"
                onClick={() => set({ board: form.board.filter((_, x) => x !== i) })}
              >
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && (
        <button
          className="btn btn--ghost"
          onClick={() => set({ board: [...form.board, { name: '', role: '' }] })}
        >
          + Add board member
        </button>
      )}

      {error && <p className="recform__error company__error">{error}</p>}

      {!readOnly && (
        <div className="company__actions">
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save company details'}
          </button>
        </div>
      )}
    </div>
  )
}

export { EMPTY_COMPANY }
